import { type IconToken } from '@1debit/icons';
import { type PropsWithChildren } from 'react';

import { type IconProps } from '../ActivityIndicator/subcomponents/icon';
import { getIconByKey } from '../utils/assets';
import { classNames } from '../utils/classNames';

type CircledIconProps = {
	icon: IconToken;
	size: IconProps['size'];
	padding: string;
	variant?: 'default' | 'inverse' | 'alwaysDark';
	className?: string;
	disabled?: boolean;
};

const VARIANT_BASE_STYLES = {
	default: 'text-content-primary bg-bg-shade',
	inverse: 'dark text-content-primary bg-bg-shade [.dark_&]:text-content-inverse [.dark_&]:bg-shade-primary-100',
	alwaysDark: 'text-content-dark bg-bg-shade [.dark_&]:bg-shade-primary-100',
};

const VARIANT_HOVER_STYLES = {
	default: 'hover:bg-bg-shade-hover',
	inverse: 'hover:bg-bg-shade-hover [.dark_&]:hover:bg-bg-pressed-overlay',
	alwaysDark: 'hover:bg-bg-pressed-overlay [.dark_&]:hover:bg-bg-pressed-overlay',
};

export const CircledIcon = ({
	icon,
	size,
	padding,
	variant,
	className,
	children,
	disabled,
}: PropsWithChildren<CircledIconProps>) => {
	const Icon = getIconByKey(icon);

	return (
		<div
			className={classNames(
				'w-fit transition-all rounded-full relative',
				!disabled && 'hover:bg-bg-raised-secondary cursor-pointer active:scale-95',
				padding,
				variant && !disabled && 'active:opacity-60',
				variant && VARIANT_BASE_STYLES[variant],
				variant && !disabled && VARIANT_HOVER_STYLES[variant],
				disabled && 'opacity-30',
				className,
			)}>
			{children}
			{Icon && <Icon aria-hidden size={size} data-testid={`icon-button-${icon}-icon`} />}
		</div>
	);
};
